import React from 'react';
import EditorField from './_EditorField';
import DataBindingPanel from '../DataBindingPanel';
import DataSectionPanel from './DataSectionPanel';
import { useTheme } from '../../../theme/ThemeContext';

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function ColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button key={p.color + p.label} type="button" title={p.label} onClick={() => onChange(p.color)}
          style={{ width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0, cursor: 'pointer',
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            background: p.color === '' ? 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 0 0 / 10px 10px' : p.color,
          }} />
      ))}
      <input type="color" value={value && value !== '' ? value : '#ffffff'} onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      {value && value !== '' && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value !== '' && <button type="button" onClick={() => onChange('')} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Reset</button>}
    </div>
  );
}

function SegmentedControl({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      {options.map((opt, i) => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
          style={{ flex: 1, padding: '5px 0', border: 'none', borderRight: i < options.length - 1 ? '1px solid #e2e8f0' : 'none',
            background: value === opt.value ? '#1e293b' : '#fff', color: value === opt.value ? '#fff' : '#374151',
            fontSize: 11, fontWeight: value === opt.value ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function DataTimelineEditor({ section, dispatch, appId }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const update = patch => dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  const updateBinding = patch => dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch });

  const primaryHex = theme.primary || '#4e2e80';
  const DOT_PRESETS = [
    { label: 'Primary', color: primaryHex },
    { label: 'Green',   color: '#16a34a'  },
    { label: 'Blue',    color: '#2563eb'  },
    { label: 'Orange',  color: '#ea580c'  },
    { label: 'Dark',    color: '#1e293b'  },
  ];

  const tableSlug = section.data_binding?.table_slug;

  return (
    <div>
      <DataBindingPanel appId={appId} binding={section.data_binding} onChange={updateBinding} />

      <SectionDivider label="Timeline Style" />

      <EditorField label="Sort Order">
        <select className="form-select form-select-sm" value={cfg.order || 'desc'} onChange={e => update({ order: e.target.value })}>
          <option value="desc">Newest First</option>
          <option value="asc">Oldest First</option>
        </select>
      </EditorField>

      <EditorField label="Connector Color">
        <ColorPicker value={cfg.connector_color || primaryHex} onChange={val => update({ connector_color: val })} presets={DOT_PRESETS} />
      </EditorField>

      <EditorField label="Dot Style">
        <SegmentedControl
          value={cfg.dot_style || 'filled'}
          onChange={val => update({ dot_style: val })}
          options={[
            { value: 'filled',   label: 'Filled'   },
            { value: 'outlined', label: 'Outlined'  },
            { value: 'icon',     label: 'Icon'      },
          ]}
        />
      </EditorField>

      <EditorField label="Card Style">
        <select className="form-select form-select-sm" value={cfg.card_style || 'default'} onChange={e => update({ card_style: e.target.value })}>
          <option value="default">Default (white)</option>
          <option value="elevated">Elevated (shadow)</option>
          <option value="outlined">Outlined</option>
          <option value="minimal">Minimal (no border)</option>
        </select>
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="showDate"
            checked={cfg.show_date !== false} onChange={e => update({ show_date: e.target.checked })} />
          <label className="form-check-label" htmlFor="showDate" style={{ fontSize: 13 }}>Show date on cards</label>
        </div>
      </EditorField>

      <DataSectionPanel cfg={cfg} updateCfg={update} tableSlug={tableSlug} />
    </div>
  );
}
